import { useState, useEffect } from "react";
import styled from "styled-components";
import ImageUploading from "react-images-uploading";
import { t } from "@lingui/core/macro";
import { isValidMarkerId, addWatermarkText } from "./Utility.js";
import { isMobile } from "react-device-detect";

const theme = {
  blue: {
    default: "#3f51b5",
    hover: "#283593",
  },
  pink: {
    default: "#e91e63",
    hover: "#ad1457",
  },
};

const UploadButton = styled.button`
  background-color: ${(props) => theme[props.theme].default};
  color: white;
  padding: 5px 15px;
  border-radius: 3px;
  outline: 0;
  border: 0;
  text-transform: uppercase;
  margin: 0.5vh 0.5vw;
  cursor: pointer;
  box-shadow: 0px 1px 1px lightgray;
  transition: ease background-color 250ms;
  &:hover {
    background-color: ${(props) => theme[props.theme].hover};
  }
  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
`;

UploadButton.defaultProps = {
  theme: "pink",
};

const Link = styled.a`
  display: inline;
  align-items: center;
  color: #bf4f74;
`;

const Icon = styled.svg`
  flex: none;
  transition: fill 0.25s;
  width: 2vh;

  ${Link}:hover & {
    fill: rebeccapurple;
  }
`;

/**
 * A component that displays an upload box
 *
 * @param {object} props
 * @returns {JSX.Element}
 */
export function UploadBox(props) {
  const [images, setImages] = useState([]);
  const maxNumber = 69;

  /**
   * A function that handles an image download
   * @param {object} e - An event dom object
   * @returns {void} Open an image link
   */
  const handleClickDownloadImage = (event) => {
    const imgUrl = event.target.src;
    window.open(imgUrl, "_blank").focus();
  };

  /**
   * A function that handles an image update
   * @param {object} e - A list of images
   * @param {number} e - The image index
   * @returns {void} Set variables of state
   */
  const onChange = async (imageList, addUpdateIndex) => {
    // if imagelist is not empty and addUpdateIndex also, is the case of a single upload
    if (imageList.length && addUpdateIndex) {
      const imageUploaded = await props.onUploadedImage(
        imageList[addUpdateIndex[0]]
      );
      imageList[addUpdateIndex[0]] = {
        ...imageList[addUpdateIndex[0]],
        ...{ publicId: imageUploaded.public_id },
      };
    }
    //update imagelist and the the UI
    setImages(imageList);
  };

  /**
   * A function to delete a single image
   * @param {object} e - An index image
   * @returns {void} Call a prop function to delete an image
   */
  const deleteImageOnChange = async (deleteIndex) => {
    const imageDeleted = isValidMarkerId(deleteIndex)
      ? await props.onDeletedImage(
          images[deleteIndex]["publicId"],
          images[deleteIndex]["id"]
        )
      : null;
    console.log("Just deleted image: " + imageDeleted);
  };

  // delete all images
  /**
   * A function that deletes all images
   * @returns {void} Call a prop function to delete all images
   */
  const deleteAllImagesOnChange = async () => {
    images.map(async (aData, index) => {
      const imageDeleted = await props.onDeletedImage(
        images[index]["publicId"],
        images[index]["id"]
      );
      console.log("Just deleted image: " + imageDeleted);
    });
  };

  // Set images from props
  useEffect(() => {
    if (props && props.images) {
      setImages(props.images);
    }
  }, [props]);

  return (
    <div>
      <ImageUploading
        multiple
        value={images}
        onChange={onChange}
        maxNumber={maxNumber}
        dataURLKey="data_url"
        inputProps={{ capture: "environment" }}
      >
        {({
          imageList,
          onImageUpload,
          onImageRemoveAll,
          onImageUpdate,
          onImageRemove,
          isDragging,
          dragProps,
        }) => (
          // Write your building UI
          <div className="upload__image-wrapper">
            <UploadButton
              disabled={props.disabled}
              style={isDragging ? { color: "red" } : undefined}
              onClick={onImageUpload}
              {...dragProps}
            >
              {isMobile ? t`Take a photo` : t`Click or Drop here`}
            </UploadButton>
            &nbsp;
            <UploadButton
              disabled={props.disabled}
              theme="blue"
              onClick={() => {
                deleteAllImagesOnChange();
                onImageRemoveAll();
              }}
            >{t`Remove all images`}</UploadButton>
            <hr />
            {imageList.map((image, index) => (
              <div key={index} className="image-item">
                <img
                  style={{ cursor: "pointer" }}
                  onClick={handleClickDownloadImage}
                  src={addWatermarkText(image["data_url"], image["w3wText"])}
                  alt=""
                  width="100"
                  height="100"
                />
                <div className="image-item__btn-wrapper">
                  <Link
                    disabled={props.disabled}
                    href="#"
                    onClick={(images) => {
                      deleteImageOnChange(index);
                      onImageRemove(index);
                    }}
                  >
                    <Icon viewBox="0 0 21 21">
                      <path
                        d="M10 12L14 16M14 12L10 16M18 6L17.1991 18.0129C17.129 19.065 17.0939 19.5911 16.8667 19.99C16.6666 20.3412 16.3648 20.6235 16.0011 20.7998C15.588 21 15.0607 21 14.0062 21H9.99377C8.93927 21 8.41202 21 7.99889 20.7998C7.63517 20.6235 7.33339 20.3412 7.13332 19.99C6.90607 19.5911 6.871 19.065 6.80086 18.0129L6 6M4 6H20M16 6L15.7294 5.18807C15.4671 4.40125 15.3359 4.00784 15.0927 3.71698C14.8779 3.46013 14.6021 3.26132 14.2905 3.13878C13.9376 3 13.523 3 12.6936 3H11.3064C10.477 3 10.0624 3 9.70951 3.13878C9.39792 3.26132 9.12208 3.46013 8.90729 3.71698C8.66405 4.00784 8.53292 4.40125 8.27064 5.18807L8 6"
                        stroke="#ffffff"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></path>{" "}
                    </Icon>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </ImageUploading>
    </div>
  );
}

export default UploadBox;
